import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import recetaService, { RecetaListItemDto } from '../../services/recetaService';
import RecetaCodigoLookupField from '../../pages/produccion/RecetaCodigoLookupField';
import RecetaSearchDialog from '../../pages/produccion/RecetaSearchDialog';

interface Props {
  label?: string;
  value: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  helperText?: string;
  dialogTitle?: string;
  onChange: (codigo: string) => void;
}

const GranjaRecetaCodigoField: React.FC<Props> = ({
  label = 'Receta',
  value,
  disabled,
  size = 'small',
  helperText,
  dialogTitle = 'Buscar receta',
  onChange,
}) => {
  const [codigoInput, setCodigoInput] = useState(value);
  const [descripcion, setDescripcion] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const applyRequestIdRef = useRef(0);
  const hintRequestIdRef = useRef(0);

  useEffect(() => {
    setCodigoInput(value);
  }, [value]);

  useEffect(() => {
    const code = value.trim();
    if (!code) {
      setDescripcion('');
      return;
    }

    const requestId = ++hintRequestIdRef.current;
    (async () => {
      try {
        const receta = await recetaService.getReceta(code);
        if (requestId !== hintRequestIdRef.current) return;
        setDescripcion(String(receta.descripcion ?? '').trim());
        if (receta.codigoReceta?.trim()) setCodigoInput(receta.codigoReceta.trim());
      } catch {
        if (requestId !== hintRequestIdRef.current) return;
        setDescripcion('');
      }
    })();
  }, [value]);

  const recetaResuelta =
    value.trim().length > 0 &&
    codigoInput.trim().toUpperCase() === value.trim().toUpperCase();

  const aplicarReceta = useCallback(
    async (codigo: string) => {
      const trimmed = codigo.trim();
      const requestId = ++applyRequestIdRef.current;

      if (!trimmed) {
        onChange('');
        setDescripcion('');
        setError(null);
        return;
      }

      setError(null);
      try {
        const receta = await recetaService.getReceta(trimmed);
        if (requestId !== applyRequestIdRef.current) return;

        const codigoOk = receta.codigoReceta.trim();
        setCodigoInput(codigoOk);
        setDescripcion(String(receta.descripcion ?? '').trim());
        onChange(codigoOk);
      } catch {
        if (requestId !== applyRequestIdRef.current) return;
        setDescripcion('');
        setError(`No se encontró la receta ${trimmed}.`);
      }
    },
    [onChange],
  );

  const handleSelect = (item: RecetaListItemDto) => {
    applyRequestIdRef.current += 1;
    hintRequestIdRef.current += 1;
    setError(null);
    setCodigoInput(item.codigoReceta);
    setDescripcion(String(item.descripcion ?? '').trim());
    onChange(item.codigoReceta);
  };

  const handleResolveExact = (codigo: string) => {
    const trimmed = codigo.trim();
    if (!trimmed) {
      onChange('');
      setDescripcion('');
      return;
    }
    onChange(trimmed);
    if (
      value.trim().length > 0 &&
      trimmed.toUpperCase() === value.trim().toUpperCase() &&
      descripcion
    ) {
      return;
    }
    void aplicarReceta(trimmed);
  };

  const tooltipTitle =
    !error && descripcion ? `${(value || codigoInput).trim()} — ${descripcion}` : '';

  return (
    <>
      <Tooltip title={tooltipTitle} placement="top" enterDelay={400}>
        <span style={{ display: 'block', width: '100%' }}>
          <RecetaCodigoLookupField
            label={label}
            size={size}
            value={codigoInput}
            descripcionHint={!error && descripcion ? descripcion : undefined}
            recetaResuelta={recetaResuelta}
            disabled={disabled}
            error={Boolean(error)}
            helperText={error || (!descripcion ? helperText : undefined)}
            onChange={(raw) => {
              setError(null);
              setCodigoInput(raw);
              if (!raw.trim()) {
                onChange('');
                setDescripcion('');
              } else if (descripcion && raw.trim().toUpperCase() !== value.trim().toUpperCase()) {
                setDescripcion('');
              }
            }}
            onSelect={handleSelect}
            onResolveExact={handleResolveExact}
            onOpenDialog={() => setDialogOpen(true)}
          />
        </span>
      </Tooltip>

      <RecetaSearchDialog
        open={dialogOpen}
        title={dialogTitle}
        initialSearchTerm={codigoInput}
        onClose={() => setDialogOpen(false)}
        onSelect={handleSelect}
      />
    </>
  );
};

export default GranjaRecetaCodigoField;
